import * as H from "history";
import * as _ from "lodash";
import * as React from "react";
import {
  Router,
  Switch
} from "react-router-dom";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import * as Sentry from "@sentry/react";

import tokenizeString, { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { breakpoint } from "src/library/utils/styled-media-queries/index";
import { useGoogleFontStyle } from "src/library/components/GlobalStyles/FontStyles";
import Grid from "src/library/components/Hedron";
import GlobalContextModal from "src/core/shared/components/GlobalContextModal";
import { useAuth } from "src/core/shared/api/auth";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import { MenuContext } from "src/core/shared/hooks/menu/useMenu";
import { SidebarProvider } from "src/context/SideBarContext/SidebarProvider";
import SmartNotification from "src/modules/smart/components/SmartNotification";
import { QuickViewProductProvider } from "src/core/products/components/QuickViewProduct";
import { useDefaultCardNotification } from "src/modules/payment/hooks/useDefaultCardNotification";
import { useMasqueradeModeNotification } from "src/core/shared/hooks/useMasqueradeModeNotification";
import { useDropSuspendedNotification } from "src/core/drops/hooks/useDropSuspendedNotice";
import { useNotificationBannerContext } from "src/core/shared/context/NotificationBanner/NotificationBannerContext";

import Route from "./Route";

interface IStateProps {
  user?: any;
}

interface IProps extends IStateProps {
  history: H.History<any>;
  terminology: ITerminology;
}

/**
 * App router
 */
const AppRouter: React.ReactFCWithChildren<IProps> = React.memo(props => {
  const { history, terminology } = props;
  const [ { checkLoggedIn }, { loggedIn, loggingIn } ] = useAuth(false, history);

  useDefaultCardNotification();
  useMasqueradeModeNotification(history);
  useDropSuspendedNotification();

  const {
    user, role, theme
  } = useMappedState(({ global }: IStore) => ({
    user: global.user,
    role: global.role,
    error: global.error,
    theme: global.settings?.theme
  }));

  useGoogleFontStyle(theme);

  const [ { routes: menuRoutes } ] = React.useContext(
    MenuContext
  );

  const [ , { checkDropIsSynced } ] = useDrop();

  // On mount
  React.useEffect(() => {
    checkLoggedIn().then(isLoggedIn => {
      if (isLoggedIn && role && role.type === "customer") {
        checkDropIsSynced();
      }
    }).catch(error => {
      Sentry.captureException(error);
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RootWrapper className="root-wrapper">
      <RootContent
        className="root-content"
        direction="horizontal"
      >
        <Router history={history}>
          <QuickViewProductProvider>
            <SidebarProvider>
              {user && <SmartNotification userId={user.id} />}

              <Switch>
                {!loggingIn &&
              menuRoutes.map((route, i) => {
                if (route.pageComponent) {
                  return (
                    <Route
                      terminology={terminology}
                      key={`${route}-${i}`}
                      {...route}
                      title={tokenizeString(route.title, terminology)}
                      authenticated={loggedIn}
                    />
                  );
                }

                return null;
              })}
              </Switch>

              {props.children}
            </SidebarProvider>
          </QuickViewProductProvider>
        </Router>

      </RootContent>

      <GlobalContextModal
        routes={menuRoutes}
        history={history}
      />
    </RootWrapper>
  );
});

const RootWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const RootContent = styled(Grid.Bounds)`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;
    flex-direction: column;
    
    ${breakpoint("md", theme)} {
      flex-direction: row;
    }
  `}
`;

export default AppRouter;
